import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { trackProductEvent } from "@/app/infra/product-events"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { Flow } from "@/instrumentation/logger/types"
import type { NftMiniature } from "@anaconda/core/nft/types"
import type { CurrencyId } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import {
  type ItemBidRequest,
  itemBidFactoryReatom,
  parseUnionAddress,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import Big from "big.js"
import { molecule } from "bunshi"
import type { Address } from "viem"
import { BlockchainBalanceSdkProviderMoleculeReatom } from "../../blockchains/blockchain-balance-sdk-provider.reatom"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import {
  trackTransactionCompleted,
  trackTransactionFailed,
  trackTransactionStarted,
} from "../events/track-transaction"
import { getTransactionErrorMessage } from "../events/transaction-event"
import { invalidateNftOffersQueries } from "../order/invalidate-nft-offers-queries"

export interface EvmItemOfferActionParams {
  nft: NftMiniature
  currency: CurrencyId
  price: string
}

export const EvmItemOfferTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)
  const { evmAddress: evmAddressAtom } = mol(WalletConnectMoleculeReatom)
  const { balanceSDKAtom } = mol(BlockchainBalanceSdkProviderMoleculeReatom)
  const { reatomWrappedCurrencyId } = mol(BlockchainsMoleculeReatom)

  const itemOfferActionFactory = () => {
    const itemOfferAction = action(
      async ({ nft, currency, price }: EvmItemOfferActionParams) => {
        const container = sdkContainerAtom()
        const balanceSDK = balanceSDKAtom()
        const from = evmAddressAtom()

        if (!from) {
          throw new Error("Wallet address is not available for item offer")
        }

        const blockchain = nft.collection.blockchain
        const { address, tokenId } = parseUnionAddress(nft.id)
        if (!tokenId) {
          throw new Error(`Item ${nft.id} has no token id`)
        }

        // The SDK signs non-wrapped ERC20 bids with the wrong denomination.
        if (currency !== reatomWrappedCurrencyId()(blockchain)?.id) {
          throw new Error("Item offers must be made in the wrapped currency")
        }

        // Same pre-check as a collection offer: the SDK deposits native
        // currency into the wrapped token when the wrapped balance is short.
        let balances: [string, string] | undefined
        try {
          if (balanceSDK) {
            balances = await wrap(
              Promise.all([
                balanceSDK.getTokenBalanceByCurrency({
                  blockchain,
                  currencyId: currency,
                  address: from as Address,
                }),
                balanceSDK.getNativeBalance({
                  blockchain,
                  address: from as Address,
                }),
              ]),
            )
          }
        } catch (error) {
          console.warn("Failed to get balance for validation:", error)
        }

        if (balances) {
          const [wrappedBalance, nativeBalance] = balances
          if (Big(price).gt(Big(wrappedBalance).plus(nativeBalance))) {
            throw new Error("Error: insufficient funds for item offer")
          }
        }

        const itemBid = itemBidFactoryReatom(container)
        const request: ItemBidRequest = {
          from: from as Address,
          blockchain,
          collectionId:
            `${blockchain}:${address}` as ItemBidRequest["collectionId"],
          tokenId,
          currencyId: currency,
          priceValue: price,
          originFees: [],
        }

        const result = await wrap(itemBid(request))

        return { txId: result.txId, nft }
      },
      "evmItemOfferAction",
    ).extend(
      withAsync(),
      // The dialog closes on submit, so the pending toast is the only feedback.
      withCallHook((_, [{ nft }]) => {
        sonnerToast.custom(
          (id) => (
            <AppToast.Pending
              id={id}
              title="Offer"
              main={<Trans>Offer</Trans>}
              second={<Trans>Pending...</Trans>}
            />
          ),
          { duration: Number.POSITIVE_INFINITY },
        )
        trackTransactionStarted({ logger, flow: Flow.MakeOffer, nft })
      }),
    )

    itemOfferAction.onReject.extend(
      withCallHook(({ error, params: [{ nft }] }) => {
        console.error("Item offer failed:", error)
        trackTransactionFailed({ logger, flow: Flow.MakeOffer, nft, error })

        const message = getTransactionErrorMessage(error)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Error
            id={id}
            title="Offer"
            main={<Trans>Offer failed</Trans>}
            second={
              <div className="font-semibold text-destructive">{message}</div>
            }
          />
        ))
      }),
    )

    itemOfferAction.onFulfill.extend(
      withCallHook(({ payload }) => {
        const { txId, nft } = payload
        // The factory waits until the order is active, so the row is indexed.
        invalidateNftOffersQueries(nft.id)
        trackTransactionCompleted({ logger, flow: Flow.MakeOffer, nft, txId })
        trackProductEvent("nft_offer_created", {
          blockchain: nft.collection.blockchain,
        })

        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Success
            id={id}
            title="Offer"
            main={<Trans>Offer sent</Trans>}
            second={
              <SuccessViewTransaction
                transactionId={txId}
                blockchain={nft.collection.blockchain}
              />
            }
          />
        ))
      }),
    )

    return itemOfferAction
  }

  return { itemOfferActionFactory }
})
